import React from 'react';
import { marked } from 'marked';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBook, faPaperPlane, faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { Message } from '../../types';
import './MessageItem.css';

interface Props {
  message: Message;
  onCitationClick: (citation: string) => void;
  onFollowupClick: (question: string) => void;
}

export const MessageItem: React.FC<Props> = ({ message, onCitationClick, onFollowupClick }) => {
  const { t } = useTranslation();
  const htmlContent = marked(message.content, { async: false }) as string;

  const isExternalLink = (citation: string): boolean => {
    // Debug logging
    console.log('Checking citation:', citation);
    
    // Check if it's a URL
    const isUrl = citation.startsWith('http://') || citation.startsWith('https://');
    console.log('Is URL:', isUrl);
    
    // If it's not a URL, it's an internal document
    if (!isUrl) {
      console.log('Not a URL, treating as internal document');
      return false;
    }
    
    // If it's a URL, check if it's a PDF
    const isPdf = citation.toLowerCase().endsWith('.pdf');
    console.log('Is PDF:', isPdf);
    
    // If it's a URL but not a PDF, it's an external link
    // If it's a PDF URL, we need to check if it's internal or external
    const isInternalPdf = citation.includes('/api/documents/');
    console.log('Is internal PDF:', isInternalPdf);
    
    // It's an external link if:
    // 1. It's a URL but not a PDF, OR
    // 2. It's a PDF URL but not from our internal API
    return isUrl && (!isPdf || !isInternalPdf);
  };

  // Helper function to check if a file is a PDF
  const isPdfFile = (filename: string): boolean => {
    return filename.toLowerCase().endsWith('.pdf');
  };

  // Helper function to extract filename from citation
  const getFilenameFromCitation = (citation: string): string => {
    // If the citation is a full URL to our API, extract just the document path
    if (citation.includes('/api/documents/')) {
      return citation.split('/api/documents/')[1];
    }
    return citation;
  };

  const handleCitationClick = (citation: string) => {
    console.log('Handling citation click:', citation);
    const external = isExternalLink(citation);
    console.log('Is external link:', external);
    
    if (external) {
      // For external links, open in new tab
      window.open(citation, '_blank', 'noopener,noreferrer');
    } else {
      // Extract the filename
      const filename = getFilenameFromCitation(citation);
      
      // Check if it's a PDF
      if (isPdfFile(filename)) {
        // For PDFs, use the PDF viewer
        onCitationClick(filename);
      } else {
        // For other document types (Word, Excel, PowerPoint), create a download link
        const downloadUrl = `${window.location.origin}/api/documents/${filename}`;
        
        // Create a temporary anchor element to trigger download
        const downloadLink = document.createElement('a');
        downloadLink.href = downloadUrl;
        downloadLink.setAttribute('download', filename); // This is key for triggering download
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
      }
    }
  };

  return (
    <div className={`message ${message.role}-message`}>
      <div className="message-content" dangerouslySetInnerHTML={{ __html: htmlContent }} />
      
      {message.citations && message.citations.length > 0 && (
        <div className="citations">
          <div className="citations-header">{t('chat.sources')}:</div>
          <div className="citations-list">
            {message.citations.map((citation, index) => {
              const external = isExternalLink(citation);
              return (
                <div key={index} className="citation-item">
                  <button
                    className={`citation-button ${external ? 'external-link' : ''}`}
                    onClick={() => handleCitationClick(citation)}
                    title={external ? t('chat.openInNewTab') : t('chat.viewDocument')}
                  >
                    <FontAwesomeIcon 
                      icon={external ? faExternalLinkAlt : faBook} 
                      style={{ marginRight: '8px' }} 
                    />
                    {citation}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}
      
      {message.followupQuestions && message.followupQuestions.length > 0 && (
        <div className="followup-questions">
          <div className="followup-header">{t('chat.followupQuestions')}:</div>
          <div className="followup-list">
            {message.followupQuestions.map((question, index) => (
              <div key={index} className="followup-item">
                <button
                  className="followup-button"
                  onClick={() => onFollowupClick(question)}
                  title={t('chat.useQuestion')}
                >
                  <FontAwesomeIcon icon={faPaperPlane} style={{ marginRight: '8px' }} />
                  {question}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}; 